/**
 * Settings Service - Manages user preferences and configuration
 *
 * This service handles CRUD operations for user settings, providing
 * a centralized interface for managing application configuration
 * stored in Firestore.
 *
 * Key Responsibilities:
 * - Load and save user settings to/from Firestore
 * - Provide default settings for new users
 * - Validate settings before persistence
 * - Cache settings for performance
 * - Handle offline scenarios gracefully
 *
 * Integration:
 * - Uses shared firestoreService for database operations
 * - Publishes settings change events via eventBridgeService
 * - Provides settings to all application components
 */

import { db, eventBridgeService } from '@trip-planner-monorepo/shared';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';

class SettingsService {
  constructor() {
    this.isInitialized = false;
    this.settingsCache = new Map(); // Cache user settings for performance
    this.defaultSettings = {
      backup: {
        directory: 'Downloads/TripPlannerBackups',
        format: 'json',
        autoBackup: false,
        retentionDays: 30
      },
      ui: {
        theme: 'light',
        language: 'en',
        dateFormat: 'MM/DD/YYYY'
      },
      privacy: {
        analytics: true,
        crashReporting: true
      }
    };
  }

  /**
   * Initialize the settings service
   */
  init() {
    if (this.isInitialized) {
      console.warn('SettingsService already initialized');
      return;
    }

    // Register event listeners
    eventBridgeService.subscribe('USER_LOGGED_IN', this.handleUserLogin.bind(this));
    eventBridgeService.subscribe('USER_LOGGED_OUT', this.handleUserLogout.bind(this));

    this.isInitialized = true;
    console.log('SettingsService initialized');
  }

  /**
   * Handle user login - preload settings
   * @param {Object} event - Login event
   */
  async handleUserLogin(event) {
    const { userId } = event.payload;
    if (userId) {
      try {
        await this.getUserSettings(userId);
        console.log(`Settings preloaded for user ${userId}`);
      } catch (error) {
        console.warn(`Failed to preload settings for user ${userId}:`, error);
      }
    }
  }

  /**
   * Handle user logout - clear cache
   * @param {Object} event - Logout event
   */
  handleUserLogout(event) {
    const { userId } = event.payload;
    if (userId && this.settingsCache.has(userId)) {
      this.settingsCache.delete(userId);
      console.log(`Settings cache cleared for user ${userId}`);
    }
  }

  /**
   * Get user settings from cache or Firestore
   * @param {string} userId - User identifier
   * @returns {Object} User settings or defaults
   */
  async getUserSettings(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Check cache first
    if (this.settingsCache.has(userId)) {
      return this.settingsCache.get(userId);
    }

    try {
      // Fetch from Firestore
      const settingsRef = doc(db, 'userSettings', userId);
      const settingsDoc = await getDoc(settingsRef);

      let userSettings;
      if (settingsDoc.exists()) {
        userSettings = settingsDoc.data();

        // Merge with defaults to handle new settings added over time
        userSettings = this.mergeWithDefaults(userSettings);
      } else {
        // New user - create default settings
        userSettings = this.createDefaultSettings();
        await this.updateUserSettings(userId, userSettings);
      }

      // Cache the settings
      this.settingsCache.set(userId, userSettings);

      return userSettings;

    } catch (error) {
      console.error(`Failed to get settings for user ${userId}:`, error);

      // Return defaults if Firestore is unavailable
      const defaultSettings = this.createDefaultSettings();
      this.settingsCache.set(userId, defaultSettings);
      return defaultSettings;
    }
  }

  /**
   * Update user settings in Firestore and cache
   * @param {string} userId - User identifier
   * @param {Object} settings - Complete settings object
   * @returns {Promise<void>}
   */
  async updateUserSettings(userId, settings) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!settings || typeof settings !== 'object') {
      throw new Error('Valid settings object is required');
    }

    try {
      // Validate settings structure
      const validatedSettings = this.validateSettings(settings);

      // Add metadata
      const settingsWithMetadata = {
        ...validatedSettings,
        updatedAt: new Date().toISOString(),
        version: '1.0.0'
      };

      // Save to Firestore
      const settingsRef = doc(db, 'userSettings', userId);
      await setDoc(settingsRef, settingsWithMetadata);

      // Update cache
      this.settingsCache.set(userId, validatedSettings);

      // Publish settings updated event
      eventBridgeService.publish('USER_SETTINGS_UPDATED', {
        userId,
        settings: validatedSettings,
        timestamp: new Date().toISOString()
      });

      console.log(`Settings updated for user ${userId}`);

    } catch (error) {
      console.error(`Failed to update settings for user ${userId}:`, error);
      throw new Error(`Settings update failed: ${error.message}`);
    }
  }

  /**
   * Update specific setting section
   * @param {string} userId - User identifier
   * @param {string} section - Settings section (backup, ui, privacy)
   * @param {Object} sectionData - New data for the section
   * @returns {Promise<void>}
   */
  async updateSettingsSection(userId, section, sectionData) {
    const currentSettings = await this.getUserSettings(userId);

    const updatedSettings = {
      ...currentSettings,
      [section]: {
        ...currentSettings[section],
        ...sectionData
      }
    };

    await this.updateUserSettings(userId, updatedSettings);
  }

  /**
   * Get specific setting value
   * @param {string} userId - User identifier
   * @param {string} path - Setting path (e.g., 'backup.directory')
   * @returns {Promise<any>} Setting value
   */
  async getSetting(userId, path) {
    const settings = await this.getUserSettings(userId);
    return this.getNestedValue(settings, path);
  }

  /**
   * Set specific setting value
   * @param {string} userId - User identifier
   * @param {string} path - Setting path (e.g., 'backup.directory')
   * @param {any} value - New value
   * @returns {Promise<void>}
   */
  async setSetting(userId, path, value) {
    const settings = await this.getUserSettings(userId);
    const updatedSettings = this.setNestedValue(settings, path, value);
    await this.updateUserSettings(userId, updatedSettings);
  }

  /**
   * Reset user settings to defaults
   * @param {string} userId - User identifier
   * @returns {Promise<void>}
   */
  async resetUserSettings(userId) {
    const defaultSettings = this.createDefaultSettings();
    await this.updateUserSettings(userId, defaultSettings);
  }

  /**
   * Delete user settings
   * @param {string} userId - User identifier
   * @returns {Promise<void>}
   */
  async deleteUserSettings(userId) {
    try {
      const settingsRef = doc(db, 'userSettings', userId);
      await deleteDoc(settingsRef);
      this.settingsCache.delete(userId);

      eventBridgeService.publish('USER_SETTINGS_DELETED', {
        userId,
        timestamp: new Date().toISOString()
      });

      console.log(`Settings deleted for user ${userId}`);

    } catch (error) {
      console.error(`Failed to delete settings for user ${userId}:`, error);
      throw new Error(`Settings deletion failed: ${error.message}`);
    }
  }

  /**
   * Create default settings object
   * @returns {Object} Default settings
   */
  createDefaultSettings() {
    return JSON.parse(JSON.stringify(this.defaultSettings));
  }

  /**
   * Merge user settings with defaults
   * @param {Object} userSettings - User's existing settings
   * @returns {Object} Merged settings
   */
  mergeWithDefaults(userSettings) {
    const defaults = this.createDefaultSettings();
    return this.deepMerge(defaults, userSettings);
  }

  /**
   * Validate settings structure and values
   * @param {Object} settings - Settings to validate
   * @returns {Object} Validated settings
   */
  validateSettings(settings) {
    const validated = { ...settings };

    // Validate backup settings
    if (validated.backup) {
      if (validated.backup.directory && typeof validated.backup.directory !== 'string') {
        throw new Error('Backup directory must be a string');
      }

      if (validated.backup.format && !['json'].includes(validated.backup.format)) {
        throw new Error('Invalid backup format');
      }
    }

    // Validate UI settings
    if (validated.ui) {
      if (validated.ui.theme && !['light', 'dark'].includes(validated.ui.theme)) {
        validated.ui.theme = 'light';
      }

      if (validated.ui.language && typeof validated.ui.language !== 'string') {
        validated.ui.language = 'en';
      }
    }

    // Validate privacy settings
    if (validated.privacy) {
      if (typeof validated.privacy.analytics !== 'boolean') {
        validated.privacy.analytics = true;
      }

      if (typeof validated.privacy.crashReporting !== 'boolean') {
        validated.privacy.crashReporting = true;
      }
    }

    return validated;
  }

  /**
   * Deep merge two objects
   * @param {Object} target - Target object
   * @param {Object} source - Source object
   * @returns {Object} Merged object
   */
  deepMerge(target, source) {
    const result = { ...target };

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          result[key] = this.deepMerge(result[key] || {}, source[key]);
        } else {
          result[key] = source[key];
        }
      }
    }

    return result;
  }

  /**
   * Get nested value from object using dot notation
   * @param {Object} obj - Object to search
   * @param {string} path - Dot-separated path
   * @returns {any} Value at path
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Set nested value in object using dot notation
   * @param {Object} obj - Object to modify
   * @param {string} path - Dot-separated path
   * @param {any} value - Value to set
   * @returns {Object} Modified object
   */
  setNestedValue(obj, path, value) {
    const result = { ...obj };
    const keys = path.split('.');
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return result;
  }

  /**
   * Clear settings cache (useful for testing)
   */
  clearCache() {
    this.settingsCache.clear();
  }

  /**
   * Get cache status for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStatus() {
    return {
      size: this.settingsCache.size,
      users: Array.from(this.settingsCache.keys())
    };
  }
}

// Create singleton instance
const settingsService = new SettingsService();

export { settingsService };
export default settingsService;